import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, Briefcase, IndianRupee, Upload } from 'lucide-react';
import { getJob, submitApplication } from '../services/api';
import toast from 'react-hot-toast';

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', coverLetter: '' });
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    getJob(id).then(res => { setJob(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    const data = new FormData();
    data.append('jobId', id);
    data.append('name', form.name);
    data.append('email', form.email);
    data.append('phone', form.phone);
    data.append('coverLetter', form.coverLetter);
    if (file) data.append('resume', file);
    try {
      await submitApplication(data);
      toast.success('Application submitted successfully!');
      setShowApply(false);
      setForm({ name: '', email: '', phone: '', coverLetter: '' });
      setFile(null);
    } catch {
      toast.error('Failed to submit application');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="loading" style={{ minHeight: '60vh' }}><div className="spinner"></div></div>;
  if (!job) return <div className="empty-state" style={{ minHeight: '60vh' }}><h3>Job not found</h3></div>;

  return (
    <>
      <div className="page-header">
        <h1>{job.title}</h1>
        <p>{job.company}</p>
        <div className="breadcrumb">
          <Link to="/">Home</Link> / <Link to="/jobs">Jobs</Link> / <span>{job.title}</span>
        </div>
      </div>
      <div className="job-detail">
        <div className="job-detail-card">
          <div className="job-detail-header">
            <div>
              <h1>{job.title}</h1>
              <div className="company">{job.company}</div>
            </div>
            <button className="btn-apply" onClick={() => setShowApply(true)}
              style={{ padding: '14px 32px', fontSize: '1rem' }}>
              Apply Now
            </button>
          </div>
          <div className="job-detail-meta">
            <div className="job-meta-item"><MapPin size={18} /> {job.location}</div>
            <div className="job-meta-item"><Clock size={18} /> {job.experience}</div>
            <div className="job-meta-item"><Briefcase size={18} /> {job.type}</div>
            <div className="job-meta-item"><IndianRupee size={18} /> {job.salary}</div>
          </div>
          <div className="job-detail-section">
            <h3>Job Description</h3>
            <p>{job.description}</p>
          </div>
          {job.requirements && (
            <div className="job-detail-section">
              <h3>Requirements</h3>
              <p>{job.requirements}</p>
            </div>
          )}
          {job.category && (
            <div className="job-detail-section">
              <h3>Category</h3>
              <span className="job-type-badge">{job.category}</span>
            </div>
          )}
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <button className="btn-apply" onClick={() => setShowApply(true)}
              style={{ padding: '16px 48px', fontSize: '1.05rem' }}>
              Apply for this Position
            </button>
          </div>
        </div>
      </div>

      {showApply && (
        <div className="modal-overlay" onClick={() => setShowApply(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Apply for {job.title}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
                </div>
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Cover Letter</label>
                <textarea value={form.coverLetter} onChange={(e) => setForm({...form, coverLetter: e.target.value})}
                  placeholder="Tell us why you're a great fit..." />
              </div>
              <div className="form-group">
                <label>Resume</label>
                <div className="file-upload" onClick={() => fileRef.current.click()}>
                  <Upload size={32} color="#94a3b8" />
                  <p>Click to upload your resume (PDF, DOC, DOCX)</p>
                  {file && <div className="file-name">{file.name}</div>}
                  <input type="file" ref={fileRef} accept=".pdf,.doc,.docx"
                    onChange={(e) => setFile(e.target.files[0])} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowApply(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
